import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useQuery, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, RouteLoadingIndicator } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import { Button } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import SFSpinner from '@web/src/components/primatives/sf-spinner/sf-spinner'
import Footer from '@web/src/features/footer/footer'
import NotificationItem from '@web/src/features/notifications/notification-item/notification-item'
import {
  type NotificationsListFilterFormValues,
  type NotificationsListSearchState,
  notificationsListFilterDefaultsFromSearch,
  notificationsListMobileFilterDefaultsFromSearch,
} from '@web/src/features/notifications/notifications-list-filter-types'
import NotificationsListSideNav from '@web/src/features/notifications/notifications-list-side-nav/notifications-list-side-nav'
import NotificationsListTopNav from '@web/src/features/notifications/notifications-list-top-nav/notifications-list-top-nav'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { SquareCheckBigIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ProfileBasicInfo, ProfileMediaInfo } from 'src/lib/fragments'
import { READ_ALL_NOTIFICATIONS } from 'src/lib/mutations'
import type { NotificationsCursorQuery, NotificationsCursorQueryVariables } from 'types/graphql'
import { z } from 'zod'

const NOTIFICATIONS_QUERY: TypedDocumentNode<NotificationsCursorQuery, NotificationsCursorQueryVariables> = gql`
  query NotificationsCursorQuery($filter: NotificationFilterInput, $cursor: String) {
    notificationsCursor(filter: $filter, cursor: $cursor) {
      entries {
        id
        createdAt
        type
        category
        templateMessage
        templateUrl
        readAt
        data
        targetId
        creator {
          ...ProfileBasicInfo
        }
      }
      count
      cursor
    }
  }
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

export const Route = createFileRoute('/(auth)/(main-layout)/notifications')({
  component: NotificationsPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    creatorId: z.string().optional(),
    read: z.boolean().optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        creatorId: '',
        sortOrder: 'desc',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Notifications | ${env.VITE_APP_NAME}`,
      description: 'Notifications Page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loaderDeps: ({ search: { creatorId, read } }) => ({ creatorId, read }),
  loader: async ({ context, deps }) => {
    const variables: NotificationsCursorQueryVariables = {
      filter: {
        creatorId: deps.creatorId || undefined,
        read: deps.read,
      },
    }

    const { queryRef } = await queryAndPreload(context, NOTIFICATIONS_QUERY, { variables })
    return { queryRef }
  },
})

function NotificationsPage() {
  const { t } = useTranslation()
  const search = useSearch({ strict: false }) as NotificationsListSearchState
  const navigate = useNavigate()
  const apolloClient = useApolloClient()
  const { queryRef } = Route.useLoaderData()
  const {
    data: readData,
    error: readError,
    networkStatus: readNetworkStatus,
  } = useReadQuery(queryRef as QueryRef<NotificationsCursorQuery, NotificationsCursorQueryVariables>)

  const params = {
    creatorId: search.creatorId || '',
    read: search.read !== undefined ? search.read : undefined,
    sortOrder: search.sortOrder ?? 'desc',
  }

  const queryResult = useQuery<NotificationsCursorQuery, NotificationsCursorQueryVariables>(NOTIFICATIONS_QUERY, {
    variables: {
      filter: {
        creatorId: params.creatorId || undefined,
        read: params.read,
      },
    },
    notifyOnNetworkStatusChange: true,
  })

  const data = readData || queryResult.data
  const error = readError || queryResult.error
  const networkStatus = readNetworkStatus === NetworkStatus.loading ? readNetworkStatus : queryResult.networkStatus

  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [readAllConfirmOpen, setReadAllConfirmOpen] = useState(false)
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)

  const searchRef = useRef(search)
  searchRef.current = search

  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  const isFilterLoading = filterNavigatePending && isRoutePending

  const sideNavFilterDefaults = useMemo(() => notificationsListFilterDefaultsFromSearch(search), [search])

  const topNavFilterDefaults = useMemo(() => notificationsListMobileFilterDefaultsFromSearch(search), [search])

  const sortFormMethods = useForm<{ sortOrder: 'asc' | 'desc' }>({
    defaultValues: { sortOrder: params.sortOrder },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortOrder: params.sortOrder })
  }, [params.sortOrder, sortFormMethods])

  const refetchNotifications = () => {
    return apolloClient.refetchQueries({ include: [NOTIFICATIONS_QUERY] })
  }

  const [readAllNotifications] = useMutation(READ_ALL_NOTIFICATIONS, {
    onCompleted: () => {
      refetchNotifications()
      setShowSpinnerValue(false)
      setReadAllConfirmOpen(false)
      toast.success(t('All notifications marked as read'))
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinnerValue(false)
    },
  })

  const handleConfirmReadAll = () => {
    setShowSpinnerValue(true)
    readAllNotifications()
  }

  const handleFetchMore = () => {
    const notificationsCursor = data?.notificationsCursor
    if (!notificationsCursor?.cursor) return

    queryResult.fetchMore({
      variables: {
        cursor: notificationsCursor.cursor,
        filter: {
          creatorId: params.creatorId || undefined,
          read: params.read,
        },
      },
    })
  }

  const applyFilters = useCallback(
    (values: NotificationsListFilterFormValues) => {
      setFilterNavigatePending(true)
      navigate({
        to: '/notifications',
        search: {
          ...searchRef.current,
          creatorId: values.creatorId || undefined,
          read: values.readFilter === 'all' ? undefined : values.readFilter === 'read',
        },
        replace: true,
      } as never)
    },
    [navigate]
  )

  const applySortOrder = useCallback(
    (sortOrder: 'asc' | 'desc') => {
      navigate({
        to: '/notifications',
        search: {
          ...searchRef.current,
          sortOrder: sortOrder === 'desc' ? undefined : sortOrder,
        },
        replace: true,
      } as never)
    },
    [navigate]
  )

  const handleDesktopSortChange = useCallback(() => {
    applySortOrder(sortFormMethods.getValues('sortOrder'))
  }, [applySortOrder, sortFormMethods])

  const handleResetFilters = useCallback(() => {
    navigate({ to: '/notifications', replace: true } as never)
  }, [navigate])

  const sortedEntries = useMemo(() => {
    const entries = data?.notificationsCursor?.entries
    if (!entries) {
      return []
    }
    const next = [...entries]
    const mult = params.sortOrder === 'asc' ? 1 : -1
    next.sort((a, b) => mult * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()))
    return next
  }, [data?.notificationsCursor?.entries, params.sortOrder])

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.notificationsCursor) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 lg:px-16">
        <p className="text-center text-muted-foreground">{t('No notifications found')}</p>
      </div>
    )
  }

  const notificationsCursor = data.notificationsCursor

  return (
    <>
      <div className="flex w-full flex-col md:flex-row">
        <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
          <NotificationsListSideNav
            filterDefaults={sideNavFilterDefaults}
            onFiltersApply={applyFilters}
            onReset={handleResetFilters}
          />
        </aside>

        <div className="flex w-full min-w-0 flex-1 flex-col">
          <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />

          <div className="w-full max-w-full px-4 py-2 md:px-8 lg:px-16">
            <div className="hidden pt-4 md:flex md:flex-row md:items-center md:justify-end md:gap-3">
              <span className="shrink-0 text-nowrap font-medium text-muted-foreground text-sm">{t('Sort By:')}</span>
              <Form formMethods={sortFormMethods} suppressHydrationWarning>
                <NativeSelectFormField name="sortOrder" onValueChange={handleDesktopSortChange}>
                  <option value="desc">{t('Newest first')}</option>
                  <option value="asc">{t('Oldest first')}</option>
                </NativeSelectFormField>
              </Form>
              <Button
                disabled={notificationsCursor.entries.length === 0 || showSpinner}
                onClick={() => setReadAllConfirmOpen(true)}
                type="button"
              >
                <SquareCheckBigIcon className="mr-2 size-4" />
                {t('Mark all as read')}
              </Button>
            </div>

            <div className="mb-6 w-full md:hidden">
              <NotificationsListTopNav
                filterDefaults={topNavFilterDefaults}
                onFiltersApply={applyFilters}
                onReset={handleResetFilters}
                onSortOrderApply={applySortOrder}
                rightBarChildren={
                  <Button
                    aria-label={t('Mark all as read')}
                    disabled={notificationsCursor.entries.length === 0 || showSpinner}
                    onClick={() => setReadAllConfirmOpen(true)}
                    size="icon"
                    type="button"
                  >
                    <SquareCheckBigIcon className="size-4" />
                  </Button>
                }
              />
            </div>

            <div className="flex flex-col pt-2 pb-8 md:pt-4">
              <div className="w-full overflow-hidden rounded-lg border border-border">
                <InfiniteScroll
                  dataLength={notificationsCursor.entries.length}
                  hasMore={!!notificationsCursor.cursor}
                  loader={
                    <div className="flex justify-center py-2">
                      <SFSpinner className="my-2" size="sm" />
                    </div>
                  }
                  next={handleFetchMore}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="flex flex-col gap-0">
                    {sortedEntries.map((notification) => (
                      <div
                        className={notification.readAt ? 'border-border border-b' : 'border-border border-b bg-card'}
                        key={notification.id}
                      >
                        <NotificationItem notification={notification} refetchQuery={NOTIFICATIONS_QUERY} />
                      </div>
                    ))}

                    {notificationsCursor.entries.length === 0 && (
                      <p className="py-10 text-center text-muted-foreground">
                        {t('No notifications match the current filters')}
                      </p>
                    )}
                  </div>
                </InfiniteScroll>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Mark all as read')}
        icon={<SquareCheckBigIcon />}
        loading={showSpinner}
        onConfirm={handleConfirmReadAll}
        onOpenChange={(details) => {
          setReadAllConfirmOpen(details.open)
          if (!details.open) {
            setShowSpinnerValue(false)
          }
        }}
        open={readAllConfirmOpen}
        text={t('This will mark every notification as read. You cannot undo this action.')}
        title={t('Mark all as read?')}
        variant="basic"
      />
    </>
  )
}
